import React, { useState } from 'react';
import {
  Dialog,
  Grid,
  Button,
  Typography,
  Skeleton,
  DialogContent,
} from '@mui/material';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import { poppins400, poppins500 } from 'theme/common.style';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PdfIcon from 'assets/images/pdficon.png';
import { PDFDocument } from 'pdf-lib';
import Loading from 'assets/images/loading.png';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { ReactComponent as Filemanagericon } from 'assets/svg/Paper.svg';
import { ComputerIcon } from 'component/BuyersMarketplace/Marketplaceicons';
import DocumentHub from 'component/FileManager/DocumentHub';

function FinalContractUploadDialog({
  open,
  onClose,
  onSubmit,
  streetName,
  matchessm,
  buyerId,
  userType,
  isVendorContainer,
  propertyIdbanner,
  getListInfo,
}) {
  const [finalContractFile, setFinalContractFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOptimized, setIsOptimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = React.useRef(null);
  const iconRef = React.useRef(null);
  const [openFileManager, setOpenFileManager] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const openMenu = Boolean(anchorEl);
  const handleUploadClick = (event) => {
    if (isProcessing) return;
    setAnchorEl(iconRef.current);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isProcessing) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isProcessing) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  // Function to check if PDF might cause FPDI issues
  const checkPdfForFpdiCompatibility = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Check PDF header
      const header = new TextDecoder().decode(uint8Array.slice(0, 10));
      if (!header.includes('%PDF')) {
        return {
          compatible: false,
          message: 'Not a valid PDF file. Please upload a valid PDF document.',
          requiresOptimization: false,
        };
      }

      // Check file size (large files often contain images)
      const fileSizeMB = file.size / (1024 * 1024);

      // Read first 50KB to check for compression markers
      const chunkSize = Math.min(50 * 1024, file.size);
      const chunk = new TextDecoder().decode(uint8Array.slice(0, chunkSize));

      // Check for problematic compression methods
      const hasProblematicCompression =
        chunk.includes('/JBIG2Decode') || // Advanced image compression
        chunk.includes('/JPXDecode') || // JPEG2000 compression
        chunk.includes('/CCITTFaxDecode'); // Fax compression

      // Check for image markers
      const hasImages =
        chunk.includes('/XObject') ||
        chunk.includes('/Image') ||
        chunk.includes('/DCTDecode');

      if (hasProblematicCompression) {
        return {
          compatible: false,
          requiresOptimization: true,
        };
      }

      if (fileSizeMB > 5 || hasImages) {
        return {
          compatible: true,
          requiresOptimization: true,
        };
      }

      return {
        compatible: true,
        message: '',
        requiresOptimization: false,
      };
    } catch (error) {
      return {
        compatible: false,
        message: 'Unable to analyze PDF. Please try a different file.',
        requiresOptimization: false,
      };
    }
  };

  // Function to optimize PDF for FPDI compatibility
  const optimizePdfForFpdi = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();

      // Load PDF with pdf-lib
      const pdfDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: true,
        updateMetadata: true,
      });

      // Flatten the PDF (remove any forms or annotations)
      const pages = pdfDoc.getPages();

      // Simple optimization: just re-save with pdf-lib
      // This often removes problematic compression
      const optimizedBytes = await pdfDoc.save({
        useObjectStreams: false, // Disable object streams
        addDefaultPage: false,
      });

      // Create optimized file
      const optimizedBlob = new Blob([optimizedBytes], {
        type: 'application/pdf',
      });

      const optimizedFile = new File(
        [optimizedBlob],
        file.name.replace('.pdf', '.pdf') || 'contract_optimized.pdf',
        {
          type: 'application/pdf',
          lastModified: Date.now(),
        }
      );

      return {
        success: true,
        file: optimizedFile,
      };
    } catch (error) {
      return {
        success: false,
        file: file,
      };
    }
  };

  const validateAndSetFile = async (file) => {
    setErrorMessage('');
    setFinalContractFile(null);
    setIsOptimized(false);

    if (!file) return;

    if (file.type !== 'application/pdf') {
      setErrorMessage('Please upload a PDF file only.');
      return;
    }

    const maxSize = 20.5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrorMessage('File size exceeds 20MB limit.');
      return;
    }

    setIsProcessing(true);

    try {
      const compatibility = await checkPdfForFpdiCompatibility(file);

      if (!compatibility.compatible) {
        setErrorMessage(compatibility.message);
        setIsProcessing(false);
        return;
      }

      let finalFile = file;

      if (compatibility.requiresOptimization) {
        const optimization = await optimizePdfForFpdi(file);

        if (optimization.success) {
          finalFile = optimization.file;
          setIsOptimized(true);
        }
      }

      setTimeout(() => {
        setFinalContractFile(finalFile);
        setErrorMessage('');
        setIsProcessing(false);
      }, 500);
    } catch (error) {
      setErrorMessage('Unable to process PDF file.');
      setIsProcessing(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    await validateAndSetFile(file);
  };

  const handleRemoveFile = () => {
    setFinalContractFile(null);
    setErrorMessage('');
    setIsOptimized(false);
  };

  const handleSubmit = () => {
    if (finalContractFile) {
      onSubmit(finalContractFile, selectedBuyerId);
      setFinalContractFile(null);
      setSelectedBuyerId(0);
      setErrorMessage('');
      setIsOptimized(false);
    }
  };

  const handleClose = () => {
    setFinalContractFile(null);
    setErrorMessage('');
    setIsProcessing(false);
    setIsOptimized(false);
    onClose();
  };

  const getPrimaryFileType = (title) => {
    return title?.split('-')[0]?.trim() || '';
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth={false}
        fullScreen={matchessm}
        PaperProps={{
          sx: {
            borderRadius: matchessm ? 0 : '5px',
            width: matchessm ? '100vw' : '600',
            maxWidth: matchessm ? '100vw' : '600px',
            height: matchessm ? '100vh' : 'auto',
            margin: 0,
            padding: matchessm ? '20px' : '30px',
            boxShadow: matchessm ? 'none' : '-4px 4px rgba(0, 0, 0, 0.09)',
          },
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography
              sx={{
                ...poppins400,
                fontSize: matchessm ? '10px' : '14px',
                color: '#666666',
              }}
            >
              {streetName}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            padding="12px 0 15px"
          >
            <Pendingwarning />
            <Typography
              sx={{
                ...poppins500,
                fontSize: matchessm ? '16px' : '24px',
                paddingLeft: '12px',
              }}
            >
              Initiate Sign Request
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              sx={{
                ...poppins400,
                fontSize: matchessm ? '12px' : '16px',
                color: '#666666',
                lineHeight: '22px',
              }}
            >
              Upload the contract of sale document to initiate the E-sign
              request process.
            </Typography>
          </Grid>

          <Grid item xs={12} marginTop="10px">
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '12px',
              }}
            >
              Upload Final Contract <span style={{ color: 'red' }}>*</span>
            </Typography>

            {!finalContractFile ? (
              <>
                <Grid container mt={2}>
                  <input
                    ref={fileInputRef}
                    id="final-contract-upload"
                    type="file"
                    hidden
                    accept="application/pdf"
                    onChange={handleFileChange}
                    disabled={isProcessing}
                  />

                  <Grid
                    onClick={handleUploadClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: '150px',
                      height: '110px',
                      border: isDragging
                        ? '2px solid #8B5CF6'
                        : '2px dashed #8B5CF6',
                      borderRadius: '6px',
                      backgroundColor: isDragging ? '#EEE5FF' : '#F4EDFF',
                      cursor: isProcessing ? 'wait' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isProcessing ? 0.7 : 1,
                      '&:hover': {
                        backgroundColor: isProcessing ? '#F4EDFF' : '#EEE5FF',
                      },
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <Skeleton
                          variant="circular"
                          width={32}
                          height={32}
                          sx={{
                            backgroundColor: 'rgba(139, 92, 246, 0.2)',
                            marginBottom: '6px',
                          }}
                        />
                        <Skeleton
                          variant="text"
                          width={100}
                          height={20}
                          sx={{
                            backgroundColor: 'rgba(139, 92, 246, 0.2)',
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <AddCircleOutlineIcon
                          ref={iconRef}
                          sx={{
                            fontSize: '32px',
                            color: '#8B5CF6',
                            marginBottom: '6px',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#8B5CF6',
                            textAlign: 'center',
                          }}
                        >
                          Upload or Drag here
                        </Typography>
                      </>
                    )}
                  </Grid>

                  <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                      vertical: 'center',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'center',
                      horizontal: 'center',
                    }}
                    PaperProps={{
                      sx: {
                        borderRadius: '6px',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                        ml: '60px',
                        mt: '35px',
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCloseMenu();
                        fileInputRef.current.click();
                      }}
                      sx={{
                        px: 1.2,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        '&:hover': {
                          backgroundColor: '#F4EDFF',
                        },
                      }}
                    >
                      <ComputerIcon sx={{ fontSize: 16 }} />

                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '13px',
                          fontWeight: 400,
                        }}
                      >
                        From Device
                      </Typography>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleCloseMenu();
                        setOpenFileManager(true);
                      }}
                      sx={{
                        px: 1.2,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        '&:hover': {
                          backgroundColor: '#F4EDFF',
                        },
                      }}
                    >
                      <Filemanagericon width={16} height={16} />

                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '13px',
                          fontWeight: 400,
                        }}
                      >
                        File Manager
                      </Typography>
                    </MenuItem>
                  </Menu>
                </Grid>

                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '11px',
                    color: errorMessage ? '#d32f2f' : '#666666',
                    marginTop: '8px',
                    textAlign: 'center',
                    ml: errorMessage ? '' : '20px',
                  }}
                >
                  {errorMessage || 'Upload PDF file only'}
                </Typography>
              </>
            ) : (
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                spacing={0.5}
              >
                <Grid item>
                  <img
                    src={PdfIcon}
                    alt="PDF"
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'contain',
                    }}
                  />
                </Grid>

                <Grid
                  item
                  container
                  alignItems="center"
                  wrap="nowrap"
                  sx={{
                    maxWidth: '220px',
                    ml: '20px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '13px',
                      color: '#2F3F5E',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {finalContractFile.name}
                  </Typography>

                  <DeleteOutlineOutlinedIcon
                    onClick={handleRemoveFile}
                    sx={{
                      fontSize: '16px',
                      color: '#8B5CF6',
                      cursor: 'pointer',
                      marginLeft: '6px',
                      flexShrink: 0,
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            gap={matchessm ? '5px' : 2}
            marginTop={matchessm ? '20px' : '30px'}
            alignItems={'center'}
          >
            <Grid item>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isProcessing}
                sx={{
                  width: matchessm ? '110px' : '140px',
                  height: matchessm ? '35px' : '41px',
                  fontSize: matchessm ? '12px' : '14px',
                  color: '#666666',
                  fontFamily: 'Poppins',
                  border: '1px solid rgba(17, 35, 71, 0.1)',
                  borderRadius: '5px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(17, 35, 71, 0.1)',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <Button
                disabled={!finalContractFile || isProcessing}
                onClick={handleSubmit}
                sx={{
                  width: matchessm ? '110px' : '140px',
                  height: matchessm ? '35px' : '41px',
                  fontSize: matchessm ? '12px' : '14px',
                  color: '#FFFFFF',
                  fontFamily: 'Poppins',
                  background: '#4CA4D6',
                  borderRadius: '5px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4CA4D6',
                  },
                  '&:disabled': {
                    background: '#BFDDF0',
                    color: '#FFFFFF',
                    opacity: isProcessing ? 0.7 : 1,
                  },
                }}
              >
                Next
              </Button>
            </Grid>
            {isProcessing && (
              <Grid item>
                <img
                  alt="Loading"
                  src={Loading}
                  className="loaderImage"
                  style={{
                    height: '25px',
                    width: '25px',
                    marginRight: '8px',
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Dialog>
      <Dialog
        open={openFileManager}
        onClose={() => setOpenFileManager(false)}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '85%',
            maxWidth: '1200px',
          },
        }}
      >
        <DialogContent
          sx={{
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 0,
          }}
        >
          <DocumentHub
            isDialog={true}
            overrideBuyerId={isVendorContainer ? 0 : buyerId}
            isVendorContainer={isVendorContainer}
            setIsOpenFileManager={setOpenFileManager}
            onBack={() => setOpenFileManager(false)}
            propertyAddress={streetName}
            buyerId={buyerId}
            userType={userType}
            propertyIdbanner={propertyIdbanner}
            getListInfo={getListInfo}
            onSelectDocument={(selectedDocument) => {
              const fileName =
                getPrimaryFileType(selectedDocument.title) + '.pdf';
              const filePath = selectedDocument.filePath;
              fetch(filePath)
                .then((response) => response.blob())
                .then(async (blob) => {
                  const file = new File([blob], fileName, {
                    type: 'application/pdf',
                  });
                  await validateAndSetFile(file);
                  setSelectedBuyerId(selectedDocument?.buyerId);
                  setOpenFileManager(false);
                })
                .catch((error) => {
                  setErrorMessage('Failed to load the selected file');
                });
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FinalContractUploadDialog;
